import { useMutation } from '@tanstack/react-query';
import { Button, Modal, Typography } from '@volvo/vcdk-react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import type { IWifiConfigurationRequest, IWifiNetworkRow } from '@/service/vpc/types/IWifiConfiguration';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import { vpcService } from '@/service/vpc/vpcService';

type Props = {
  chassisId: string;
  row: IWifiNetworkRow | null;
  existingNetworks: IWifiNetworkRow[];
  onClose: () => void;
  onMutationSuccess: (correlationId: string, updatedNetworks: IWifiNetworkRow[]) => void;
};

const DeleteNetworkModal: React.FC<Props> = ({ chassisId, row, existingNetworks, onClose, onMutationSuccess }) => {
  const { t } = useTranslation();
  const [hasError, setHasError] = useState(false);

  const ssid = row?.ssid ?? '';

  const { mutateAsync: updateWifiConfiguration, isPending } = useMutation({
    mutationFn: ({ chassisId, configurations }: { chassisId: string; configurations: IWifiConfigurationRequest[] }) => {
      return vpcService.Services.updateWifiConfiguration({ chassisId, configurations });
    },
  });

  const handleOnDelete = async () => {
    const sortedNetworks = existingNetworks.filter((n) => n.ssid !== ssid).sort((a, b) => a.priority - b.priority);
    const configurations: IWifiConfigurationRequest[] = sortedNetworks.map((n) => ({
      ssid: n.ssid,
      priority: n.priority,
    }));

    try {
      const result = await updateWifiConfiguration({ chassisId, configurations });
      onMutationSuccess(result.correlationId, sortedNetworks);
      onClose();
    } catch {
      setHasError(true);
    }
  };

  if (!row) return null;

  return (
    <>
      {createPortal(
        <Modal open={!!row} onClose={onClose} maxWidth="90%" width={500}>
          <span slot="header">{t(($) => $.components.modal.add_network_modal.remove_network)}</span>
          <Typography variant="body">
            {t(($) => $.components.modal.add_network_modal.remove_network_description)}
          </Typography>
          <span slot="footer">
            <div className="flex gap-4">
              <Button variant="secondary" onClick={onClose} disabled={isPending}>
                {t(($) => $.common.cancel)}
              </Button>
              <Button variant="primary" onClick={handleOnDelete} disabled={isPending}>
                {t(($) => $.components.modal.add_network_modal.remove_network)}
              </Button>
            </div>
          </span>
        </Modal>,
        document.body
      )}
      {hasError &&
        createPortal(
          <Modal open={hasError} onClose={() => setHasError(false)} maxWidth="90%" width={500}>
            <span slot="header">{t(($) => $.components.modal.add_network_modal.error_title)}</span>
            <Typography variant="body">{t(($) => $.components.modal.add_network_modal.error_description)}</Typography>
            <span slot="footer">
              <Button variant="primary" onClick={() => setHasError(false)}>
                {t(($) => $.common.close)}
              </Button>
            </span>
          </Modal>,
          document.body
        )}
    </>
  );
};

export default withComponentErrorBoundary(DeleteNetworkModal);
